import * as Yup from 'yup';

import {
  BOUserTypeEnum,
  EntityInfoNumberTypeEnum,
  EntityInformationResponseModel,
} from 'api/backofficeApiClient';
import {
  maxTextInputCharactersLength,
  einMask,
  ssnMask,
} from 'utils/validationUtils';
import { getMaskedNumberValue } from './numberUtils';

interface EditEntityInfoFormValuesModel {
  legalCompanyName: string;
  w8w9FilePath: string[];
}

interface EditCompanyInfoFormValuesModel extends EditEntityInfoFormValuesModel {
  ein: string;
}

interface EditSelfEmployedInfoFormValuesModel
  extends EditEntityInfoFormValuesModel {
  taxIdOption: number;
  ssn?: string;
  ein?: string;
}

interface EditInternationalInfoFormValuesModel
  extends EditEntityInfoFormValuesModel {
  foreignTIN: string;
  incorporationCountry: string;
}

export type EditEntityInfoFormValues = EditCompanyInfoFormValuesModel &
  EditSelfEmployedInfoFormValuesModel &
  EditInternationalInfoFormValuesModel;

export interface EditEntityInfoSettings {
  validationSchema: Yup.SchemaOf<EditEntityInfoFormValues>;
  initialValues: EditEntityInfoFormValues;
}

export const isSsnOption = (option?: number) =>
  option === EntityInfoNumberTypeEnum.Ssn;

export const isEinOption = (option?: number) =>
  option === EntityInfoNumberTypeEnum.Ein;

export const getEinTypeLabel = (type?: EntityInfoNumberTypeEnum) => {
  switch (type) {
    case EntityInfoNumberTypeEnum.Ein:
      return 'EIN';

    case EntityInfoNumberTypeEnum.Ssn:
      return 'SSN';

    default:
      return '';
  }
};

const companyValidationSchema: Yup.SchemaOf<EditCompanyInfoFormValuesModel> =
  Yup.object({
    legalCompanyName: Yup.string()
      .trim()
      .required('Field is required')
      .max(
        maxTextInputCharactersLength,
        `Legal company name should be ${maxTextInputCharactersLength} characters maximum`,
      ),
    ein: Yup.string()
      .trim()
      .required('Field is required')
      .length(10, 'Ein is invalid'),
    w8w9FilePath: Yup.array()
      .of(Yup.string().defined())
      .min(1, 'Field is required'),
  });

const selfEmployedValidationSchema: Yup.SchemaOf<EditSelfEmployedInfoFormValuesModel> =
  Yup.object({
    legalCompanyName: Yup.string()
      .trim()
      .required('Field is required')
      .max(
        maxTextInputCharactersLength,
        `Legal name should be ${maxTextInputCharactersLength} characters maximum`,
      ),
    taxIdOption: Yup.number().nullable().required('Field is required'),
    ssn: Yup.string().when('taxIdOption', {
      is: isSsnOption,
      then: Yup.string()
        .trim()
        .required('Field is required')
        .length(11, 'SSN is invalid'),
    }),
    ein: Yup.string().when('taxIdOption', {
      is: isEinOption,
      then: Yup.string()
        .trim()
        .required('Field is required')
        .length(10, 'EIN is invalid'),
    }),
    w8w9FilePath: Yup.array()
      .of(Yup.string().defined())
      .min(1, 'Field is required'),
  });

const internationalValidationSchema: Yup.SchemaOf<EditInternationalInfoFormValuesModel> =
  Yup.object({
    legalCompanyName: Yup.string()
      .trim()
      .required('Field is required')
      .max(
        maxTextInputCharactersLength,
        `Legal company name should be ${maxTextInputCharactersLength} characters maximum`,
      ),
    foreignTIN: Yup.string()
      .trim()
      .required('Field is required')
      .max(
        maxTextInputCharactersLength,
        `Foreign TIN should be ${maxTextInputCharactersLength} characters maximum`,
      ),
    incorporationCountry: Yup.string().trim().required('Field is required'),
    w8w9FilePath: Yup.array()
      .of(Yup.string().defined())
      .min(1, 'Field is required'),
  });

const EDIT_ENTITY_INFO_SETTINGS = (
  entityInfo?: EntityInformationResponseModel,
) => ({
  [BOUserTypeEnum.Company]: {
    validationSchema: companyValidationSchema,
    initialValues: {
      legalCompanyName: entityInfo?.name || '',
      ein: getMaskedNumberValue(entityInfo?.number, einMask) || '',
      w8w9FilePath: entityInfo?.entityInfoFileKey
        ? [entityInfo?.entityInfoFileKey]
        : [],
    },
  },
  [BOUserTypeEnum.SelfEmployed]: {
    validationSchema: selfEmployedValidationSchema,
    initialValues: {
      legalCompanyName: entityInfo?.name || '',
      taxIdOption: entityInfo?.numberType || null,
      ssn: isSsnOption(entityInfo?.numberType)
        ? getMaskedNumberValue(entityInfo?.number, ssnMask)
        : '',
      ein: isEinOption(entityInfo?.numberType)
        ? getMaskedNumberValue(entityInfo?.number, einMask)
        : '',
      w8w9FilePath: entityInfo?.entityInfoFileKey
        ? [entityInfo?.entityInfoFileKey]
        : [],
    },
  },
  [BOUserTypeEnum.International]: {
    validationSchema: internationalValidationSchema,
    initialValues: {
      legalCompanyName: entityInfo?.name || '',
      foreignTIN: entityInfo?.number || '',
      incorporationCountry: entityInfo?.countryId || '',
      w8w9FilePath: entityInfo?.entityInfoFileKey
        ? [entityInfo?.entityInfoFileKey]
        : [],
    },
  },
});

const getEditEntityInfoSettings = (
  userType: BOUserTypeEnum,
  entityInfo?: EntityInformationResponseModel,
) => {
  return EDIT_ENTITY_INFO_SETTINGS(entityInfo)[
    userType
  ] as EditEntityInfoSettings;
};

export { getEditEntityInfoSettings };
